// Merchant org page — read for everyone, write for owner only.

import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError, merchantAuth, merchantOrg, type MerchantOrgType } from '@/api/client';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export const Route = createFileRoute('/m/org')({
  component: MerchantOrgPage,
});

function MerchantOrgPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['merchant-me'], queryFn: merchantAuth.me });
  const org = useQuery({ queryKey: ['merchant-org'], queryFn: merchantOrg.get });

  const isOwner = me.data?.role === 'owner';
  const [name, setName] = useState('');
  const [type, setType] = useState<MerchantOrgType>('company');

  if (org.data && name === '') setName(org.data.name);
  if (org.data && type === 'company' && org.data.type !== 'company') setType(org.data.type);

  const save = useMutation({
    mutationFn: () => merchantOrg.update({ name, type }),
    onSuccess: () => {
      toast.success(t('merchant.org.saved', { defaultValue: 'Organization updated.' }));
      qc.invalidateQueries({ queryKey: ['merchant-org'] });
      qc.invalidateQueries({ queryKey: ['merchant-me'] });
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Save failed'),
  });

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">
          {t('merchant.org.title', { defaultValue: 'Organization' })}
        </h1>
        <p className="text-sm text-muted-foreground">
          {t('merchant.org.subtitle', {
            defaultValue:
              'Your merchant org details. The merchant code is set by your administrator and cannot be changed here.',
          })}
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>{t('merchant.org.profile', { defaultValue: 'Org profile' })}</CardTitle>
          <CardDescription>
            {!isOwner &&
              t('merchant.org.read_only', {
                defaultValue: 'Only the owner role can edit. Ask your owner if you need changes.',
              })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="space-y-4 max-w-md"
            onSubmit={(e) => {
              e.preventDefault();
              if (!isOwner) return;
              save.mutate();
            }}
          >
            <div className="space-y-1.5">
              <Label htmlFor="code">{t('merchants.code')}</Label>
              <Input id="code" value={org.data?.code ?? ''} disabled readOnly />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="name">{t('merchants.name', { defaultValue: 'Name' })}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!isOwner}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('merchant.org.type', { defaultValue: 'Type' })}</Label>
              <Select
                value={type}
                onValueChange={(v) => setType(v as MerchantOrgType)}
                disabled={!isOwner}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="person">
                    {t('merchant.org.person', { defaultValue: 'Person (sole proprietor)' })}
                  </SelectItem>
                  <SelectItem value="company">
                    {t('merchant.org.company', { defaultValue: 'Company' })}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('merchants.status', { defaultValue: 'Status' })}</Label>
              <div>
                <Badge variant="secondary" className="text-[10px] uppercase">
                  {org.data?.status}
                </Badge>
              </div>
            </div>
            {isOwner && (
              <Button type="submit" disabled={save.isPending}>
                {save.isPending
                  ? t('common.saving', { defaultValue: 'Saving…' })
                  : t('common.save', { defaultValue: 'Save' })}
              </Button>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
